// src/App.js
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import MainLayout from './components/layout/MainLayout';
import HomePage from './components/pages/HomePage';
import PrivacyPolicy from './components/pages/PrivacyPolicy';
import Impressum from './components/pages/Impressum';
import ScrollToTop from './components/common/ScrollToTop';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { initAnalytics, trackPageView } from './services/analyticsService';
import './assets/styles/topbar.css';

// Import styles in the correct order to avoid cascade issues
import './assets/styles/fonts.css'; // Import typography first
import './index.css';              // Import base styles
import './assets/styles/main.css';  // Import main styles 
import './assets/styles/language-selector.css';
import './assets/styles/contact-form.css';

// Initialize analytics as early as possible
initAnalytics();

// AppContent component with access to language context and tracking
const AppContent = () => {
  const { language } = useLanguage();
  const location = useLocation();
  
  // PageView-Tracking for each route change, but only if Analytics is loaded
  useEffect(() => {
    trackPageView(location.pathname, document.title);
  }, [location]);
  
  // Listen for the analytics-loaded event
  useEffect(() => {
    const handleAnalyticsLoaded = () => {
      // Track pageview immediately after analytics loads
      trackPageView(location.pathname, document.title);
    };
    
    // Add event listener
    document.addEventListener('analytics-loaded', handleAnalyticsLoaded);
    
    // Cleanup
    return () => {
      document.removeEventListener('analytics-loaded', handleAnalyticsLoaded);
    };
  }, [location]);
  
  return (
    <Routes>
      {/* Language routes - redirect to language-specific URLs */}
      <Route path="/" element={<Navigate to={`/${language}`} replace />} />
      
      {/* Routes for English */}
      <Route path="/en" element={<HomePage section="home" />} />
      <Route path="/en/services" element={<HomePage section="services" />} />
      <Route path="/en/portfolio" element={<HomePage section="portfolio" />} />
      <Route path="/en/about" element={<HomePage section="about" />} />
      <Route path="/en/team" element={<HomePage section="team" />} />
      <Route path="/en/contact" element={<HomePage section="contact" />} />
      <Route path="/en/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/en/impressum" element={<Impressum />} />
      
      {/* Routes for German */}
      <Route path="/de" element={<HomePage section="home" />} />
      <Route path="/de/services" element={<HomePage section="services" />} />
      <Route path="/de/portfolio" element={<HomePage section="portfolio" />} />
      <Route path="/de/about" element={<HomePage section="about" />} />
      <Route path="/de/team" element={<HomePage section="team" />} />
      <Route path="/de/contact" element={<HomePage section="contact" />} />
      <Route path="/de/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/de/impressum" element={<Impressum />} />
      
      {/* Fallback redirects for old links */}
      <Route path="/services" element={<Navigate to={`/${language}/services`} replace />} />
      <Route path="/portfolio" element={<Navigate to={`/${language}/portfolio`} replace />} />
      <Route path="/about" element={<Navigate to={`/${language}/about`} replace />} />
      <Route path="/team" element={<Navigate to={`/${language}/team`} replace />} />
      <Route path="/contact" element={<Navigate to={`/${language}/contact`} replace />} />
      <Route path="/privacy-policy" element={<Navigate to={`/${language}/privacy-policy`} replace />} />
      <Route path="/impressum" element={<Navigate to={`/${language}/impressum`} replace />} />
      
      {/* Catch-all redirect for 404 pages */}
      <Route path="*" element={<Navigate to={`/${language}`} replace />} />
    </Routes>
  );
};

function App() {
  // Erzwinge einen Scroll zum Seitenanfang beim Start der App
  useEffect(() => {
    // Immediate scroll
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    // Scroll again after DOM is ready
    const scrollToTop = () => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };
    
    // Multiple attempts to ensure scroll happens
    requestAnimationFrame(scrollToTop);
    setTimeout(scrollToTop, 0);
    setTimeout(scrollToTop, 100);
    setTimeout(scrollToTop, 300);
  }, []);
  
  return (
    <ThemeProvider>
      <LanguageProvider>
        <Router>
          {/* ScrollToTop-Komponente */}
          <ScrollToTop />
          <MainLayout>
            <AppContent />
          </MainLayout>
        </Router>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;